import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Pencil, Trash2, FileCheck, Award } from 'lucide-react';
import BlendsToolbar from '../components/toolbars/BlendsListToolbar';
import '../index.css';

const BlendList = () => {
  const [blends, setBlends] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // Add filter for draft/finalized

  const fetchBlends = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/blends', {
        params: { 
          page, 
          search, 
          status: filter !== 'all' ? filter : undefined 
        }
      });
      setBlends(response.data.blends || []);
      setTotalPages(response.data.totalPages || 0);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching blends:', err);
      setError('Failed to load blends');
      setBlends([]);
      setTotalPages(0);
      setLoading(false);
    }
  }, [page, search, filter]);

  useEffect(() => {
    fetchBlends();
  }, [fetchBlends]);

  const handleDelete = async (blendID) => {
    if (window.confirm('Are you sure you want to delete this blend?')) {
      try {
        await axios.delete(`/api/blends/${blendID}`);
        fetchBlends();
      } catch (err) {
        console.error('Error deleting blend:', err);
        setError('Failed to delete blend');
      }
    }
  };

  if (loading) {
    return (
      <>
        <BlendsToolbar 
          search={search} 
          onSearchChange={setSearch}
          filter={filter}
          onFilterChange={setFilter}
        />
        <div className="container-fluid mt-4">
          <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <BlendsToolbar 
        search={search} 
        onSearchChange={setSearch}
        filter={filter}
        onFilterChange={setFilter}
      />
      
      <div className="container mt-4">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
            ></button>
          </div>
        )}

        <div className="card">
          <div className="card-body p-0">
            {blends.length === 0 ? (
              <div className="alert alert-info m-3 mb-0">No blends found</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Blend Name</th>
                      <th>Blend Date</th>
                      <th>Total Volume</th>
                      <th>Final Alcohol</th>
                      <th>Components</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blends.map((blend) => (
                      <tr key={blend.blendID} className={blend.status === 'finalized' ? 'table-success' : ''}>
                        <td>
                          {blend.status === 'finalized' ? (
                            <span className="badge bg-success d-flex align-items-center">
                              <Award size={14} className="me-1" />
                              Finalized
                            </span>
                          ) : (
                            <span className="badge bg-secondary d-flex align-items-center">
                              <FileCheck size={14} className="me-1" />
                              Draft
                            </span>
                          )}
                        </td>
                        <td>{blend.blendName}</td>
                        <td>{new Date(blend.blendDate).toLocaleDateString()}</td>
                        <td>{parseFloat(blend.totalVolume).toFixed(2)} gal</td>
                        <td>{blend.finalAlc ? `${parseFloat(blend.finalAlc).toFixed(2)}%` : 'N/A'}</td>
                        <td>
                          <span className="badge bg-info">
                            {blend.componentCount} components
                          </span>
                        </td>
                        <td>
                          <div className="btn-group">
                            <Link 
                              to={`/blends/${blend.blendID}`} 
                              className="btn btn-outline-secondary btn-sm"
                              title="View Blend"
                            >
                              <Pencil size={16} />
                            </Link>
                            {blend.status !== 'finalized' && (
                              <button 
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDelete(blend.blendID)}
                                title="Delete Blend"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="card-footer">
              <nav>
                <ul className="pagination justify-content-center mb-0">
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => (
                    <li 
                      key={i} 
                      className={`page-item ${page === i + 1 ? 'active' : ''}`}
                    >
                      <button 
                        className="page-link" 
                        onClick={() => setPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .table-row-hover:hover td {
            background-color: rgba(0,0,0,.075);
        }
        .table-row-hover {
            transition: background-color 0.2s ease;
        }
        .page-link {
            cursor: pointer;
        }
        .page-item.disabled .page-link {
            cursor: not-allowed;
        }
        .btn svg {
            vertical-align: middle;
            margin-top: -2px;
        }
        .btn:hover svg {
            transform: scale(1.1);
            transition: transform 0.2s ease;
        }
        .btn:active svg {
            transform: scale(0.95);
        }
        .badge {
            font-weight: normal;
            font-size: 0.875rem;
        }
        .badge.d-flex {
            display: inline-flex !important;
        }
        .badge svg {
            vertical-align: text-top;
        }
      `}</style>
    </>
  );
};

export default BlendList;